import React from "react";
import { useSettings, useCart, useWishlist, useCustomer } from "hooks/useStore";
import { ReactComponent as Trash } from "svgs/trash.svg";
import { ReactComponent as Heart } from "svgs/fi-rs-heart.svg";
import { trackRemoveFromCart } from "analytics";
import MediaQuery from "components/media-query";
import { SCREEN_SIZES, SITE_URL, SHIPPING_PROTECTION } from "utils/constants";
import QuantityInput from "./quantity-input";
import ItemPrices from "./prices";
import useToggle from "hooks/useToggle";
import Loader from "components/loader";
import { toast } from "react-toastify";
import WishlistToast from "components/wishlist/toast";
import Link from "components/link";
import WarrantyUpsell from "./warranty-upsell";
import getProductUrl from "utils/product-url";
import ShippingProtectionItem from "./shipping-protection";

const CartItem = ({ product, warrantyProducts }) => {
	const { toggleCart, isCartOpen } = useSettings(["toggleCart", "isCartOpen"]);
	const { removeCartItem } = useCart(["removeCartItem"]);
	const { saveForLater } = useWishlist(["saveForLater"]);
	const [isRemoving, toggleRemoving] = useToggle();
	const customer = useCustomer();

	const handleRemoveItem = async () => {
		toggleRemoving();
		await removeCartItem(product.tempId);
		toggleRemoving();
		trackRemoveFromCart(product, customer);
	};

	const handleSaveForLater = async () => {
		const productUrl = `${SITE_URL}/products/${product.handle}`;
		const res = await saveForLater(product.id, product.variant?.id, productUrl);

		if (!res?.success) {
			toast.error("Product not added to wishlist");
		} else {
			toast.success(
				<WishlistToast image={product.variant?.images?.[0]}
					defaultImage={product.image?.src} listsToAdd={1} />
			);
			await removeCartItem(product.tempId);
			trackRemoveFromCart(product, customer);
		}
	};

	if (product.type === SHIPPING_PROTECTION) return (
		<ShippingProtectionItem product={product} isRemoving={isRemoving} handleRemoveItem={handleRemoveItem} />
	);

	return (
		<div className="cart-item">
			<div className="cart-item-content">
				<img className="size-24 md:size-28 lg:size-36" src={product.image?.src} alt={product.image?.altText} loading="lazy" />
				<div className="flex-1 flex flex-col gap-3">
					<div className="cart-item-header">
						<Link to={getProductUrl(product)} className="w-full" onClick={() => isCartOpen && toggleCart()}>
							<h4 className="text-left">{product.title}</h4>
						</Link>
						<div className="flex gap-2">
							<button className="bg-cream p-2 uppercase text-sm size-10 flex justify-center items-center"
								onClick={handleSaveForLater}>
								<Heart className="fill-black size-4" />
							</button>
							<button disabled={isRemoving} className="bg-cream p-2 size-10" onClick={handleRemoveItem}>
								{isRemoving ? <Loader /> : <Trash className="scale-[0.667]" />}
							</button>
						</div>
					</div>
					{product.variant.selectedOptions[0].value !== "Default Title" &&
						<span className="text-sm flex flex-wrap gap-2">
							{product.variant.selectedOptions.map(({ value }) =>
								<span key={value} className="capitalize border-r-2 pr-2 last:border-r-0 last:pr-0">{value}</span>)
							}
						</span>
					}
					{product.discounts?.length > 0 &&
						<span className="text-sm flex gap-2">
							{product.discounts.map((discount) =>
								discount?.amount > 0 && discount?.title &&
								<span key={discount?.title} className="capitalize text-xs text-pureRed tracking-wider">
									{discount.title}
								</span>)}
						</span>
					}
					<MediaQuery minWidth={SCREEN_SIZES.md}>
						<div className="hidden md:flex justify-between">
							<QuantityInput product={product} />
							<ItemPrices
								price={product.variant.discountedPrice}
								compareAtPrice={product.variant.compareAtPrice}
								currency={product.currency} />
						</div>
						<div className="mt-2">
							<WarrantyUpsell product={product} warrantyProducts={warrantyProducts} />
						</div>
					</MediaQuery>
				</div>
			</div>
			<MediaQuery maxWidth={SCREEN_SIZES.md}>
				<div className="flex md:hidden justify-between w-full">
					<QuantityInput product={product} />
					<ItemPrices
						price={product.variant.discountedPrice}
						compareAtPrice={product.variant.compareAtPrice}
						currency={product.currency} />
				</div>
				<div className="w-full">
					<WarrantyUpsell product={product} warrantyProducts={warrantyProducts} />
				</div>
			</MediaQuery>
		</div>
	);
};

export default CartItem;